import {RefObject, useEffect} from 'react';
import {PASSIVE_TOUCH_IF_POSSIBLE} from './constant';

/**
 * 카카오 웹툰에서 가져온 스크롤 이벤트 전파를 DOM에 붙혀 막는 hook입니다.
 * 참고 : https://github.com/kakaoent/kakaowebtoon-web/blob/sandbox/src/common/hook/useStopPropagationOnScroll.ts
 *
 * touchmove나 mousemove를 사용하는 부모 컴포넌트가 있을 때 ref만 스크롤 되도록 한다.
 *
 * blockMouseEvent가 true이면 받은 스크롤 요소보다 상위에 mousedown 이벤트도 전파되지 않도록 막습니다.
 * XXX: 개인 의견으로는 기본이 mouse 이벤트도 막는 거고 예외를 두는게 맞지 않나 하는 생각이 듭니다만, 이 훅을 사용하는 전체를 검증하기 힘들어 일단 옵션으로 달아둡니다.
 */
export default function useStopPropagationOnScroll(
  ref: RefObject<HTMLElement>,
  blockMouseEvent?: boolean
) {
  useEffect(() => {
    const scrollEl = ref?.current;
    function stopPropagation(e: TouchEvent | MouseEvent) {
      e.stopPropagation();
    }

    // move 이벤트만 막으니 기존에 start에서 가지고 있는 값이 남아 있어서 그런지 스크롤이 확 튀는 현상이 발생했습니다. start, down 이벤트도 넣어줍니다.
    if (scrollEl) {
      scrollEl.addEventListener(
        'touchstart',
        stopPropagation,
        PASSIVE_TOUCH_IF_POSSIBLE
      );
      scrollEl.addEventListener(
        'touchmove',
        stopPropagation,
        PASSIVE_TOUCH_IF_POSSIBLE
      );

      scrollEl.addEventListener(
        'pointerdown',
        stopPropagation,
        PASSIVE_TOUCH_IF_POSSIBLE
      );
      scrollEl.addEventListener(
        'pointermove', // mousemove는 overflow로 생기는 scroll도 막아서 pointer move를 넣어줍니다.
        stopPropagation,
        PASSIVE_TOUCH_IF_POSSIBLE
      );

      if (blockMouseEvent) {
        scrollEl.addEventListener(
          'mousedown',
          stopPropagation,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
      }
    }

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener(
          'touchstart',
          stopPropagation,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        scrollEl.removeEventListener(
          'touchmove',
          stopPropagation,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        scrollEl.removeEventListener(
          'pointerdown',
          stopPropagation,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        scrollEl.removeEventListener(
          'pointermove',
          stopPropagation,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        if (blockMouseEvent) {
          scrollEl.removeEventListener(
            'mousedown',
            stopPropagation,
            PASSIVE_TOUCH_IF_POSSIBLE
          );
        }
      }
    };
  }, [blockMouseEvent, ref]); // ref 를 입력하는건 이상하지만, lint 에러 때문에 넣는다
}
